import React from 'react';
import { Link } from 'react-router-dom';
import {
  Linkedin,
  Facebook,
  Instagram,
  Mail,
  Phone,
  MapPin,
  CheckCircle2,
} from 'lucide-react';

import { contactInfo, services, logoUrl } from '../data/mock';
import { useT } from '../i18n/LanguageContext';

const Footer = () => {
  const { t } = useT();

  const navLinks = [
    { label: t.nav.home, href: '/' },
    { label: t.nav.about, href: '/about' },
    { label: t.nav.services, href: '/services' },
    { label: t.nav.references, href: '/references' },
    { label: t.nav.international, href: '/international' },
    { label: t.nav.contact, href: '/contact' },
  ];

  const wired = [
    'courants-forts',
    'courants-faibles',
    'energies-renouvelables',
    'mobilite-electrique',
    'renovation-energetique',
    'chauffage-climatisation',
    'vrd',
    'bureau-etudes',
  ];

  return (
    <footer
      id="footer"
      className="bg-[#0b1c3a] text-white pt-20"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* =====================================================
              COMPANY
          ====================================================== */}
          <div>
            <div className="bg-white rounded-xl p-3 inline-block">
              <img
                src={logoUrl}
                alt="GPE Énergies & Services"
                className="h-12 w-auto object-contain"
              />
            </div>

            <p className="mt-5 text-white/70 text-sm leading-relaxed">
              {t.footer.about}
            </p>

           <div className="mt-5 flex items-center gap-3">

  <a
    href="https://www.linkedin.com/company/gpe-energies-services/"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="GPE LinkedIn"
    className="
      w-12 h-12
      rounded-full
      bg-white/10
      hover:bg-[#f5c518]
      hover:text-[#0b1c3a]
      flex items-center justify-center
      transition-colors
    "
  >
    <Linkedin className="w-5 h-5" />
  </a>

  <a
    href="#"
    aria-label="GPE Facebook"
    className="
      w-12 h-12
      rounded-full
      bg-white/10
      hover:bg-[#f5c518]
      hover:text-[#0b1c3a]
      flex items-center justify-center
      transition-colors
    "
  >
    <Facebook className="w-5 h-5" />
  </a>

  <a
    href="#"
    aria-label="GPE Instagram"
    className="
      w-12 h-12
      rounded-full
      bg-white/10
      hover:bg-[#f5c518]
      hover:text-[#0b1c3a]
      flex items-center justify-center
      transition-colors
    "
  >
    <Instagram className="w-5 h-5" />
  </a>

</div>
          </div>

          {/* =====================================================
              NAVIGATION
          ====================================================== */}
          <div>
            <div className="text-white font-bold mb-5">
              {t.footer.navigation}
            </div>

            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="
                      text-white/70
                      hover:text-[#f5c518]
                      text-sm
                      transition-colors
                    "
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* =====================================================
              SERVICES
          ====================================================== */}
          <div>
            <div className="text-white font-bold mb-5">
              {t.footer.services}
            </div>

            <ul className="space-y-3">
              {services.map((s) => {

                /*
                 * IMPORTANT:
                 * Use translated service title when available.
                 * Previously this was only s.title, which caused
                 * French titles to appear in Arabic and English.
                 */
                const translatedService = t.services?.[s.slug];

                const serviceTitle =
                  translatedService?.title || s.title;

                const target = wired.includes(s.slug)
                  ? `/services/${s.slug}`
                  : '/services';

                return (
                  <li key={s.slug}>
                    <Link
                      to={target}
                      className="
                        text-white/70
                        hover:text-[#f5c518]
                        text-sm
                        transition-colors
                      "
                    >
                      {serviceTitle}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* =====================================================
              CONTACT
          ====================================================== */}
          <div>
            <div className="text-white font-bold mb-5">
              {t.footer.contact}
            </div>

            <ul className="space-y-4 text-sm">

              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 text-[#f5c518]" />

                <span className="text-white/80">
                  {contactInfo.phone}
                </span>
              </li>

              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 text-[#f5c518]" />

                <span className="text-white/80">
                  {contactInfo.email}
                </span>
              </li>

              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-[#f5c518]" />

                <span className="text-white/80">
                  {contactInfo.address}
                </span>
              </li>

              <li className="flex items-center gap-2 mt-3">
                <CheckCircle2 className="w-4 h-4 text-[#f5c518]" />

                <span className="text-white/80">
                  {t.footer.response}
                </span>
              </li>

            </ul>
          </div>

        </div>

        {/* =====================================================
            COPYRIGHT
        ====================================================== */}
        <div
          className="
            mt-16
            pt-6
            pb-8
            border-t
            border-white/10
            flex
            flex-wrap
            items-center
            justify-between
            gap-4
            text-xs
            text-white/60
          "
        >
          <div>
            © {new Date().getFullYear()} GPE - Énergies &amp; Services.{' '}
            {t.footer.rights}
          </div>

          <div className="flex items-center gap-5">

           <Link
  to="/mentions-legales"
  className="hover:text-white transition-colors"
>
  {t.footer.legal}
</Link>

<Link
  to="/politique-de-confidentialite"
  className="hover:text-white transition-colors"
>
  {t.footer.privacy}
</Link>

<Link
  to="/cgv"
  className="hover:text-white transition-colors"
>
  CGV
</Link>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;